
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { MultiSelectModule } from 'primeng/multiselect';

import {
  SdsApiService,
  SdsEligibleEditor,
  SdsExposureAccessDto
} from '../services/sds-api.service';

/**
 * Admin-only widget that lives in the Business "feature tab" and manages the
 * SDS exposure-register PII allowlist. Editors not selected here cannot see
 * fødselsnummer / employee snapshot data for the business; Admin and Portal
 * users still can. Editors omitted from the allowlist still have full access
 * to non-PII SDS document operations.
 */
@Component({
  selector: 'kuba-sds-exposure-allowlist',
  imports: [ FormsModule, MultiSelectModule],
  template: `
    <section class="sds-exposure-allowlist">
      <header>
        <h3>SDS exposure register — Editor access</h3>
        <p class="hint">
          Pick the Editors of this business who may see employee identity
          numbers and other exposure-register PII. Admin and Portal users always
          have access; Editors not selected here cannot see those fields.
        </p>
      </header>

      @if (isLoading) {
        <div class="status">Loading…</div>
      }
      @if (errorMessage) {
        <div class="status error">{{ errorMessage }}</div>
      }

      @if (!isLoading && eligibleEditors.length === 0) {
        <div class="status">
          No active Editors found for this business.
        </div>
      }

      @if (!isLoading && editorOptions.length > 0) {
        <p-multiSelect
          [options]="editorOptions"
          [(ngModel)]="selectedEditorIds"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          display="chip"
          [filter]="true"
          [showClear]="true"
          defaultLabel="Select Editors"
          >
        </p-multiSelect>
      }

      <footer>
        <button
          type="button"
          class="btn btn-primary"
          [disabled]="isSaving || !isDirty"
          (click)="save()"
          >
          {{ isSaving ? 'Saving…' : 'Save' }}
        </button>
        <button
          type="button"
          class="btn"
          [disabled]="isSaving || !isDirty"
          (click)="reset()"
          >
          Cancel
        </button>
      </footer>
    </section>
    `,
  styles: [
    `
      .sds-exposure-allowlist {
        padding: 12px 0;
      }
      .sds-exposure-allowlist h3 {
        margin: 0 0 4px;
        font-size: 16px;
      }
      .sds-exposure-allowlist .hint {
        color: #666;
        margin: 0 0 12px;
      }
      .sds-exposure-allowlist .status {
        padding: 8px 0;
        color: #555;
      }
      .sds-exposure-allowlist .status.error {
        color: #b00020;
      }
      .sds-exposure-allowlist p-multiSelect {
        display: block;
        max-width: 520px;
        margin-bottom: 12px;
      }
      .sds-exposure-allowlist footer {
        display: flex;
        gap: 8px;
      }
    `]
})
export class SdsExposureAllowlistComponent implements OnChanges {
  @Input() businessId!: number;

  eligibleEditors: SdsEligibleEditor[] = [];
  editorOptions: Array<{ label: string; value: number }> = [];
  selectedEditorIds: number[] = [];
  isLoading = false;
  isSaving = false;
  errorMessage: string | null = null;

  private originallyAllowed = new Set<number>();

  constructor(private readonly sdsApi: SdsApiService) {}

  get isDirty(): boolean {
    const selected = new Set(this.selectedEditorIds);
    if (this.originallyAllowed.size !== selected.size) return true;
    for (const id of selected) {
      if (!this.originallyAllowed.has(id)) return true;
    }
    return false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessId'] && this.businessId) {
      void this.load();
    }
  }

  reset(): void {
    this.selectedEditorIds = Array.from(this.originallyAllowed);
  }

  async save(): Promise<void> {
    if (!this.businessId || this.isSaving) return;
    this.isSaving = true;
    this.errorMessage = null;
    try {
      const userIds = [...new Set(this.selectedEditorIds)];
      const result = await this.toPromise(
        this.sdsApi.setExposureAccess(this.businessId, userIds)
      );
      this.applyAccess(result);
    } catch (err: any) {
      this.errorMessage = err?.error?.title || err?.message || 'Failed to save allowlist.';
    } finally {
      this.isSaving = false;
    }
  }

  private async load(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;
    try {
      const [editors, access] = await Promise.all([
        this.toPromise(this.sdsApi.getEligibleEditors(this.businessId)),
        this.toPromise(this.sdsApi.getExposureAccess(this.businessId))
      ]);
      this.eligibleEditors = editors || [];
      this.editorOptions = this.eligibleEditors.map(editor => ({
        label: editor.Name || editor.Email || `Editor ${editor.UserId}`,
        value: editor.UserId
      }));
      this.applyAccess(access);
    } catch (err: any) {
      this.errorMessage = err?.error?.title || err?.message || 'Failed to load allowlist.';
    } finally {
      this.isLoading = false;
    }
  }

  private toPromise<T>(source: Observable<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const sub = source.subscribe({
        next: value => { resolve(value); sub.unsubscribe(); },
        error: err => reject(err)
      });
    });
  }

  private applyAccess(access: SdsExposureAccessDto | null): void {
    const ids = (access?.AllowedEditors || []).map(e => e.UserId);
    this.originallyAllowed = new Set(ids);
    this.selectedEditorIds = [...new Set(ids)];
  }
}
